import { useGetUserProfile as useGetUserProfileGenerated } from "@/api/generated";
import type {
  IdVerificationModel,
  PartnerIntentModel,
  UserProfileResponse,
} from "@/api/generated/models";
import { useAuthStore } from "@/store";

type UserProfileResponseConsents = UserProfileResponse["consents"];

const formatDate = (
  dateOnlyISOTimeString: string | undefined,
): string | undefined => {
  const [year, month, day] = dateOnlyISOTimeString?.split("-") || [];

  if (!year && !month && !day) return undefined;

  return `${month}/${day}/${year}`;
};

export type UserProfileFrontendShape = {
  email: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  sexAssignedAtBirth: string;
  genderIdentity: string;
  pronouns: string;
  residingState: string;
  timezone: string;
  streetAddress: string;
  streetAddress2: string;
  city: string;
  state: string;
  zipCode: string;
  consents: UserProfileResponseConsents;
  notificationPreferences: {
    clinic: boolean;
    nori: boolean;
  };
  idVerification: IdVerificationModel | null;
  partnerIntent: PartnerIntentModel | null;
  carrierName: string;
  bridgePayerId: string;
  planName: string;
  memberId: string;
  groupNumber: string;
  rxBin: string;
  rxPcn: string;
  rxGroup: string;
  userIsPolicyHolder: boolean;
  medicareBeneficiaryId: string;
  generalistRefactor: boolean;
  stripeCustomerId: string | null;
  lockedFields: string[];
};

export const useGetUserProfile = (options?: {
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
}) => {
  const { isAuthInitialized } = useAuthStore();

  return useGetUserProfileGenerated({
    query: {
      enabled: isAuthInitialized && (options?.enabled ?? true),
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      select: (data) => {
        if (data.kind === "missing") {
          return null;
        }
        const profile = data as UserProfileResponse;
        return {
          email: profile.email || "",
          firstName: profile.first_name || "",
          lastName: profile.last_name || "",
          preferredName: profile.preferred_name || "",
          dateOfBirth: formatDate(profile.date_of_birth ?? undefined) || "",
          sexAssignedAtBirth: profile.sex_at_birth || "",
          genderIdentity: profile.gender_identity || "",
          pronouns: profile.pronouns || "",
          residingState: profile.state_of_residence || "",
          timezone: profile.timezone || "",
          streetAddress: profile.home_address?.street_address || "",
          streetAddress2: profile.home_address?.street_address_2 || "",
          city: profile.home_address?.city || "",
          state:
            profile.home_address?.state || profile.state_of_residence || "",
          zipCode: profile.home_address?.zip_code || "",
          consents: profile.consents,
          notificationPreferences: {
            clinic: profile.notification_preferences?.clinic ?? true,
            nori: profile.notification_preferences?.nori ?? true,
          },
          idVerification: profile.id_verification || null,
          partnerIntent: profile.partner_intent ?? null,
          carrierName: profile.insurance?.carrier_name || "",
          bridgePayerId: profile.insurance?.bridge_payer_id || "",
          planName: profile.insurance?.plan_name || "",
          memberId: profile.insurance?.member_id || "",
          groupNumber: profile.insurance?.group_number || "",
          rxBin: profile.insurance?.rx_bin || "",
          rxPcn: profile.insurance?.rx_pcn || "",
          rxGroup: profile.insurance?.rx_group || "",
          userIsPolicyHolder: profile.insurance?.user_is_policy_holder ?? true,
          medicareBeneficiaryId:
            profile.insurance?.medicare_beneficiary_id || "",
          generalistRefactor: profile.generalist_refactor ?? false,
          stripeCustomerId: profile.linked_accounts?.stripe_customer_id ?? null,
          lockedFields: profile.locked_fields ?? [],
        };
      },
    },
  });
};
